/**
 * Abstracts the behavior of launching the reauthentication modal before an
 * action that requires reauth can proceed
 *
 * For example,
 *  <button onClick={doSomething}>Do Something</button>
 * becomes
 *  <RequireReauth onSuccess={doSomething}>
 *      {invokeReauth => <button onClick={invokeReauth}>Do Something</button>}
 *  </RequireReauth>
 */
import React, {useState} from "react";

import * as Cookies from "@khan/data-cookies";

import ReauthModal from "./reauth-modal.tsx";

interface Props {
    // Either an email or username
    identifier: string | null | undefined;
    children: (invokeReauth: () => unknown) => React.ReactNode;
    onSuccess: (() => unknown) | (() => Promise<unknown>);
    onFailure: () => unknown;
    hasPasswordField?: boolean;
    hasGoogleButton?: boolean;
    hasFacebookButton?: boolean;
    hasAppleButton?: boolean;
    hasMicrosoftButton?: boolean;
    showClasslinkAuthProvider?: boolean;
    continueUrl?: string;
}

const RequireReauth: React.FC<Props> = ({
    identifier,
    children,
    onSuccess,
    onFailure,
    hasPasswordField = true,
    hasGoogleButton = true,
    hasFacebookButton = true,
    hasAppleButton = true,
    hasMicrosoftButton = true,
    showClasslinkAuthProvider = true,
    continueUrl,
}) => {
    const [showReauthModal, setShowReauthModal] = useState(false);

    const onInvokeReauth = async () => {
        // This check navigates the user past the reauthentication modal. The
        // actual validity of the token is checked in the endpoints.
        if (Cookies.readCookie("did_reauth")) {
            if (onSuccess instanceof Promise) {
                await onSuccess();
            } else {
                onSuccess();
            }
        } else {
            setShowReauthModal(true);
        }
    };

    const onReauthSuccess = async () => {
        setShowReauthModal(false);
        if (onSuccess instanceof Promise) {
            await onSuccess();
        } else {
            onSuccess();
        }
    };

    if (showReauthModal) {
        return (
            <>
                <ReauthModal
                    identifier={identifier}
                    onClose={() => setShowReauthModal(false)}
                    onSuccess={onReauthSuccess}
                    onFailure={onFailure}
                    hasPasswordField={hasPasswordField}
                    hasGoogleButton={hasGoogleButton}
                    hasFacebookButton={hasFacebookButton}
                    hasAppleButton={hasAppleButton}
                    hasMicrosoftButton={hasMicrosoftButton}
                    showClasslinkAuthProvider={showClasslinkAuthProvider}
                    continueUrl={continueUrl}
                />
                {children(onInvokeReauth)}
            </>
        );
    }

    return <>{children(onInvokeReauth)}</>;
};

export default RequireReauth;
